'use client';

import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { SITE } from '@/app/constants';
import { cn } from '@/app/utils';
import { HeroWordmark, type HeroWordmarkVariant } from '../HeroSection/HeroWordmark';
import { RevealText } from './RevealText';
import { StatBar, type StatItem } from './StatBar';

type ShowcaseStatKey =
  | 'stats.generateAndGoLabel'
  | 'stats.realtimeInteractiveLabel'
  | 'stats.resolutionLabel'
  | 'stats.smartCutsLabel';

const STAT_ARTWORK = ['Q3-1-56c8df2f.webp', 'Q3-3-47738d6b.webp'] as const;
const S1_STAT_ARTWORK = ['S1-1-390bf717.webp', 'S1-2-1a5819b6.webp'] as const;

export const getShowcaseStats = (
  site: 'china' | 'global',
  variant: 'q3' | 's1',
  translate: (key: ShowcaseStatKey) => string,
): StatItem[] => {
  const cdnBase =
    site === 'china'
      ? 'https://image01-aliyun.vidu.zone/vidu/media-asset'
      : 'https://image01.cf.vidu.studio/vidu/media-asset';
  const artwork = variant === 's1' ? S1_STAT_ARTWORK : STAT_ARTWORK;
  const labels =
    variant === 's1'
      ? [translate('stats.resolutionLabel'), translate('stats.realtimeInteractiveLabel')]
      : [translate('stats.smartCutsLabel'), translate('stats.generateAndGoLabel')];

  return [
    {
      value: labels[0],
      label: labels[0],
      image: `${cdnBase}/${artwork[0]}`,
    },
    {
      value: labels[1],
      label: labels[1],
      image: `${cdnBase}/${artwork[1]}`,
    },
  ];
};

interface ShowcaseContentProps {
  /** 字标徽章版本，默认 Q3。第三屏传 's1'。 */
  variant?: HeroWordmarkVariant;
  /** 大标题。RevealText 逐词揭示，需为纯文本。 */
  title: string;
  /** 副标题。RevealText 逐词揭示，需为纯文本。 */
  subtitle: string;
  /** 主 CTA 文案。 */
  createLabel: ReactNode;
  /** 主 CTA 跳转，默认 /home。 */
  createHref?: string;
}

/**
 * 招牌段文本内容块：字标 + 大标题 + 副标题 + 数据条（垂直居中）+ 左下角 CTA。
 * 第二屏与第三屏（S1 卡片）共用，保证各处内容与排版完全一致。
 *
 * CTA 用绝对定位钉在左下角，不参与上方内容的垂直居中；偏移量与卡片内边距（p-12 / md:p-6）
 * 对齐。因此需要一个定位过的父容器作为包含块——FeatureCard 的文案层即是（absolute inset-0）。
 */
export const ShowcaseContent = ({
  variant = 'q3',
  title,
  subtitle,
  createLabel,
  createHref = '/home',
}: ShowcaseContentProps) => {
  const t = useTranslations('home3.showcase');
  const locale = useLocale();
  // 中文标题无空格，RevealText 整句只切成一个词，遇 max-w 会在字间断行 → 中文不限宽并强制单行。
  // 英文标题靠 20ch 主动折成多行（设计如此），保持原样。
  const isZh = locale === 'zh';

  const stats = getShowcaseStats(SITE, variant, t);

  return (
    <div className="relative flex h-full w-full flex-col items-start justify-center pb-20 text-left sm:pb-14 md:pb-16">
      <HeroWordmark variant={variant} className="mb-10 h-[44px] sm:mb-5 sm:h-[24px] md:mb-6" />
      {/* kudanil 尺度：超大展示字号，紧凑行高 */}
      <RevealText
        as="h2"
        text={title}
        className={cn(
          'font-semibold text-[4rem] text-white leading-[1.1] tracking-[-0.02em] sm:text-[2rem] sm:leading-[1.12] md:text-[3rem] lg:text-[3.5rem]',
          isZh ? 'whitespace-nowrap sm:whitespace-normal' : 'max-w-[20ch] sm:max-w-full',
        )}
      />
      <RevealText
        as="p"
        text={subtitle}
        delay={0.35}
        className="mt-6 max-w-[52rem] text-[16px] text-white/70 leading-relaxed sm:mt-3 sm:line-clamp-3 sm:text-[13px] sm:leading-5 md:mt-4"
      />
      <StatBar stats={stats} className="mt-6 sm:mt-3 md:mt-4 lg:mt-6" />

      {/* CTA：立即创作/立即体验（高亮反白）+ 了解详情（幽灵按钮），钉在卡片左下角 */}
      <div className="absolute bottom-0 left-0 flex flex-wrap items-center gap-4">
        <ClientLink
          href={createHref}
          className="box-border flex h-12 cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-white px-8 font-medium text-[16px] text-black transition hover:bg-white/90 sm:h-10 sm:px-6 sm:text-[14px]"
        >
          {createLabel}
        </ClientLink>
      </div>
    </div>
  );
};
